import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { 
  ChevronRight,
  ExternalLink,
  FileText, 
  HelpCircle,
  LifeBuoy, 
  Mail, 
  MessageSquare, 
  Phone, 
  Search
} from "lucide-react";
import { useState } from "react";

const SupportPage = () => {
  const [searchQuery, setSearchQuery] = useState("");

  const faqItems = [
    {
      question: "Як додати нову квартиру?",
      answer: "Щоб додати нову квартиру, перейдіть до розділу 'Об'єкти' та натисніть кнопку 'Додати об'єкт'. Заповніть необхідну інформацію у формі та натисніть 'Зберегти'."
    },
    {
      question: "Як створити нову заявку на ремонт?",
      answer: "Для створення заявки на ремонт перейдіть до розділу 'Заявки', натисніть 'Створити заявку', оберіть об'єкт та опишіть проблему. Після цього заявка буде створена та передана відповідальній особі."
    },
    {
      question: "Як змінити налаштування сповіщень?",
      answer: "Налаштування сповіщень доступні в розділі 'Налаштування' на вкладці 'Сповіщення'. Тут ви можете налаштувати типи сповіщень та спосіб їх отримання."
    },
    {
      question: "Як додати нового орендаря?",
      answer: "Для додавання нового орендаря перейдіть до розділу 'Орендарі', натисніть 'Додати орендаря' та заповніть необхідну інформацію. Після цього ви зможете прив'язати орендаря до конкретного об'єкту."
    }
  ];

  const filteredFaq = faqItems.filter(item => 
    item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="container mx-auto p-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="p-2 bg-primary/10 rounded-full">
            <LifeBuoy className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Підтримка</h1>
            <p className="text-muted-foreground">Допомога та відповіді на поширені запитання</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Зв'язатися з нами</CardTitle>
            <CardDescription>Оберіть зручний спосіб зв'язку</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4 p-4 rounded-lg border hover:bg-accent cursor-pointer">
              <div className="p-2 bg-primary/10 rounded-full">
                <MessageSquare className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium">Чат з підтримкою</h3>
                <p className="text-sm text-muted-foreground">Отримайте миттєву відповідь</p>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>

            <div className="flex items-center gap-4 p-4 rounded-lg border hover:bg-accent cursor-pointer">
              <div className="p-2 bg-primary/10 rounded-full">
                <Mail className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium">Email підтримка</h3>
                <p className="text-sm text-muted-foreground">support@propmanage.com</p>
              </div>
              <ExternalLink className="h-5 w-5 text-muted-foreground" />
            </div>

            <div className="flex items-center gap-4 p-4 rounded-lg border hover:bg-accent cursor-pointer">
              <div className="p-2 bg-primary/10 rounded-full">
                <Phone className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium">Телефонна підтримка</h3>
                <p className="text-sm text-muted-foreground">+38 (050) 123-45-67</p>
              </div>
              <ExternalLink className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Документація</CardTitle>
            <CardDescription>Корисні матеріали та інструкції</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4 p-4 rounded-lg border hover:bg-accent cursor-pointer">
              <div className="p-2 bg-primary/10 rounded-full">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium">Посібник користувача</h3>
                <p className="text-sm text-muted-foreground">Детальна інструкція з використання системи</p>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>

            <div className="flex items-center gap-4 p-4 rounded-lg border hover:bg-accent cursor-pointer">
              <div className="p-2 bg-primary/10 rounded-full">
                <HelpCircle className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium">Відеоінструкції</h3>
                <p className="text-sm text-muted-foreground">Навчальні відео по роботі з системою</p>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Поширені запитання</CardTitle>
          <CardDescription>Знайдіть відповіді на популярні запитання</CardDescription>
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Пошук у FAQ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {filteredFaq.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger>{item.question}</AccordionTrigger>
                <AccordionContent>{item.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
};

export default SupportPage; 